import React, { useMemo } from 'react';
import {
  Box,
  CardPurseIcon,
  CardTypesEnum,
  CheckBox,
  Hint,
  Inscription,
  List,
  OverflowText,
  Toggle,
} from '@common-fe/common-fe';

import theme from '@/styles/theme';

import { CardPurseAccess } from '../ManageAccountAccessModal.types';

const CARD_PURSE_ICON_SIZE = '32px';
const LOCKED_BY_HEALTH_PLAN_HINT = 'Account is locked based on plan rules';

interface Props {
  purse: CardPurseAccess;
  isLocked: boolean;
  employeeUnlockForbidden: boolean;
  onToggle: (id: string, locked: boolean) => void;
  onSetEmployeeUnlockForbidden: (id: string, value: boolean) => void;
}

export const AccountAccessListItem: React.FC<Props> = ({
  purse,
  isLocked,
  employeeUnlockForbidden,
  onToggle,
  onSetEmployeeUnlockForbidden,
}) => {
  const isDisabled = purse.isLockedByHealthPlan;

  const toggleContent = useMemo(
    () => (
      <Box direction="row" align="center" gap="xs">
        <Toggle
          testId={`manage-account-access_item_${purse.id}`}
          name="access"
          value={!isLocked}
          twoColorsMode
          disabled={isDisabled}
          onChange={(value) => onToggle(purse.id, !value)}
        />
        <Inscription weight={500} color="textSecondary" size="small">
          {isLocked ? 'Locked' : 'Unlocked'}
        </Inscription>
      </Box>
    ),
    [isDisabled, isLocked, onToggle, purse.id]
  );

  return (
    <List.Item
      direction="row"
      height="66px"
      data-testid={`manage-account-access_item_${purse.id}`}
      border={{ color: isLocked && !isDisabled ? 'dangerBorder' : 'border1', size: 'small' }}
      background={isLocked && !isDisabled ? 'dangerContainer' : 'canvas'}
      round="button1Round"
      align="center"
      pad={{ horizontal: 'l', vertical: 'm' }}
      style={{ cursor: 'initial' }}
    >
      <Box direction="row" align="center" gap="s" margin={{ right: 's' }} style={{ minWidth: 0 }}>
        <CardPurseIcon type={purse.accountType as CardTypesEnum} size={CARD_PURSE_ICON_SIZE} />
        <Box style={{ width: '214px' }}>
          <OverflowText
            testId={`manage-account-access_item_${purse.id}_account-name`}
            title={purse.accountName}
            textStyle={{ fontWeight: 'bold', color: theme.colors.textBody }}
          />
        </Box>
      </Box>
      <Box direction="row" align="center">
        {isDisabled ? (
          <Hint hintAlign="vertical" hintElement={toggleContent}>
            <Inscription size="12px" textAlign="center">
              {LOCKED_BY_HEALTH_PLAN_HINT}
            </Inscription>
          </Hint>
        ) : (
          toggleContent
        )}
      </Box>
      <Box direction="row" align="center" margin={{ left: 'auto' }}>
        <CheckBox
          name="preventEmployeeEdit"
          role="checkbox"
          aria-label={`preventEmployeeEdit-${purse.id}`}
          checked={employeeUnlockForbidden}
          disabled={!isLocked || isDisabled}
          onChange={({ target }) => onSetEmployeeUnlockForbidden(purse.id, target.checked)}
        />
      </Box>
    </List.Item>
  );
};
